import { useId, useState } from 'react'

import { errorMessage, t, type ErrorLike } from '@ctcells/i18n'

import { AddModal } from '../../atoms/AddModal.tsx'
import { Button } from '../../atoms/Button.tsx'
import { Disclosure } from '../../atoms/Disclosure.tsx'
import { Field } from '../../atoms/Field.tsx'
import { TextInput } from '../../atoms/TextInput.tsx'
import { useSubmitValidation } from '../../atoms/useSubmitValidation.ts'
import { ValueRows } from '../../atoms/ValueRows.tsx'
import { FormSection, FormShort, ModalFooter } from '../formSections.tsx'

import '../screens.css'
import '../resource.css'

/**
 * **소모품 보충 — 「넣었습니까?」 확인 한 번**(2026-10-01 CTCells 회의 「보충 후 "넣었습니까?" 확인 팝업 → 예 선택 시
 * 보충 인식」). 경로 `POST /modules/{id}/consumables/replenishment` · 본문 `{command_id, kind, count, note?}`.
 *
 * 🔴 **「예」 한 번이 곧 기록이다.** 기본은 가득(`full` — 그 소모품의 만재 수)이고, 가득이 아닌 수를 적어야 할 때만
 *    접힌 줄 「개수 직접 입력 · 메모」를 연다. 종전 「보충 기록 추가」 창(개수 칸 · 메모 · 「추가」)이 이 흐름으로 바뀌었다 —
 *    그래서 「추가」 창 규격(제목 「… 추가」 · 바닥 「취소」「추가」)에서 빠졌다(`addFormSpec.test.tsx`). 창 틀(`AddModal` ·
 *    본문 범위 덮개 → E-STOP 이 늘 눌린다)은 그대로다.
 * 🔴 **보충은 「사람이 실제로 채웠다」는 기록이다**(append-only 원장 · 누가 언제). 누르면 바로 보내지 않고 이 창이 묻는다.
 * 🔴 **`count` 는 늘 보낸다**(빼면 모듈이 422 — 쵸단 실측 2026-09-28. 계약은 「생략 시 만재값」이라 적지만 실물을 따른다).
 *    0 이상 만재 이하.
 * 🔴 **팁은 모듈 합계 하나다** — 서버에 랙별 보충 경로가 없다. 라벨이 「팁 랙 두 개 합계」라 적는다. 랙별 수를 지어내지 않는다.
 * 🔴 **낙관적 갱신 없음** — 이 창은 요청만 보낸다. 칩 · 서랍 숫자는 응답 뒤 모듈 조회가 다시 오면 따라간다.
 * 🔴 실패(403 · 422 · 501 · 503)는 **창 안에** 사유 + 에러 코드 원문(eric 2026-09-21 「영문 코드 … 일단 노출」).
 * 🔴 권한 · 연결 끊김 · 목 모드면 「예」가 비활성 + 사유다(`disabledReason` — 숨기지 않는다).
 */
export interface ReplenishInput {
  count: number
  /** 비웠으면 키째 없다 */
  note?: string
}

export interface ReplenishView {
  kind: 'tip' | 'tc'
  /** 화면 이름(「염색기」) — 대상 장비는 고칠 수 없는 값이다 */
  moduleName: string
  /** 만재 수 — 팁이면 두 랙 합계. 「예」의 기본값이자 입력의 상한 */
  full: number
  /** 지금 잔량(모르면 `undefined`) — 참고로만 적는다 */
  current?: number | undefined
  /** 있으면 「예」가 비활성이고 이 사유가 선다(권한 · 연결 끊김 · 목 모드) */
  disabledReason?: string | undefined
  /** 보낸다. 실패면 거절(reject)한다 — 창이 그 사유를 적는다 */
  onSubmit: (input: ReplenishInput) => Promise<unknown>
}

export function SupplyReplenishDialog({
  view,
  onClose,
}: {
  view: ReplenishView
  onClose: () => void
}) {
  const noteId = useId()
  const [count, setCount] = useState(String(view.full))
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [failure, setFailure] = useState<ErrorLike | undefined>(undefined)

  const parsed = /^\d+$/u.test(count.trim()) ? Number(count.trim()) : Number.NaN
  const countOk = Number.isInteger(parsed) && parsed >= 0 && parsed <= view.full
  /*
   * 🔴 개수는 「예」를 누를 때 잰다(eric 2026-10-27 「확인 누르면.. 빠진 부분.. 채우라고」) — 틀리면 접힌 줄을 열고 그 칸에 포커스 ·
   *    칸 아래 안내. 「예」는 권한 · 연결 끊김 · 목 모드 · 전송 중만 막는다
   */
  const form = useSubmitValidation((raw: string) => {
    const value = /^\d+$/u.test(raw.trim()) ? Number(raw.trim()) : Number.NaN
    return {
      count:
        Number.isInteger(value) && value >= 0 && value <= view.full
          ? undefined
          : t('monitoring.supply.replenish.countInvalid', { max: view.full }),
    }
  })
  const countField = form.field('count')
  /** 틀린 개수로 누르면 접힌 줄을 다시 펼친다(키를 바꿔 `defaultOpen` 으로 다시 선다) — 숨은 칸에는 포커스가 못 간다 */
  const [reopen, setReopen] = useState(0)

  const yesGate =
    view.disabledReason !== undefined
      ? { disabled: true as const, disabledReason: view.disabledReason }
      : saving
        ? { disabled: true as const, disabledReason: t('reason.requestInFlight') }
        : { disabled: false as const }

  const submit = () => {
    if (saving || view.disabledReason !== undefined) return
    // 바닥 줄은 본문 밖이라 포커스 범위는 문서다 — 칸 id 는 이 창에만 있다
    if (!form.validate(count, document.body) || !countOk) {
      setReopen((n) => n + 1)
      return
    }
    setSaving(true)
    setFailure(undefined)
    const trimmed = note.trim()
    view
      .onSubmit({ count: parsed, ...(trimmed === '' ? {} : { note: trimmed }) })
      .then(() => {
        setSaving(false)
        onClose()
      })
      .catch((error: unknown) => {
        setSaving(false)
        setFailure(error ?? {})
      })
  }

  const kindName = t(`monitoring.supply.replenish.kind.${view.kind}`)
  const countLabel = t(
    view.kind === 'tip'
      ? 'monitoring.supply.replenish.countTips'
      : 'monitoring.supply.replenish.count',
  )

  return (
    <AddModal
      size="sm"
      busy={saving}
      description={t('monitoring.supply.replenish.lead')}
      onClose={onClose}
      title={t(`monitoring.supply.replenish.ask.${view.kind}`)}
    >
      <FormSection>
        <ValueRows
          rows={[
            { key: 'module', label: t('column.module'), value: view.moduleName },
            { key: 'kind', label: t('monitoring.supply.replenish.kindLabel'), value: kindName },
            ...(view.current === undefined
              ? []
              : [
                  {
                    key: 'current',
                    label: t('monitoring.supply.replenish.current'),
                    value: t('monitoring.supply.currentOfTotal', {
                      current: view.current,
                      total: view.full,
                    }),
                  },
                ]),
            {
              key: 'after',
              label: countLabel,
              /* 「예」가 보낼 수 그대로 — 가득이면 그렇게 말한다. 틀린 입력이면 접힌 줄 안의 칸이 사유를 적는다 */
              value: countOk
                ? parsed === view.full
                  ? t('monitoring.supply.replenish.fullCount', { count: parsed })
                  : String(parsed)
                : t('mask.none'),
            },
          ]}
        />
        {/* 가득이 아닌 수 · 메모 — 기본은 접힘. 접힌 채로 보내면 가득 · 메모 없음이다 */}
        <Disclosure
          defaultOpen={reopen > 0}
          key={reopen}
          label={t('monitoring.supply.replenish.manual')}
        >
          <FormShort>
            <Field label={countLabel} required {...countField.fieldProps}>
              <TextInput
                {...countField.inputProps}
                onChange={(value) => {
                  setCount(value)
                  form.change('count', value)
                }}
                type="number"
                value={count}
                wide
              />
            </Field>
          </FormShort>
          <Field htmlFor={noteId} label={t('monitoring.supply.replenish.note')}>
            <TextInput id={noteId} onChange={setNote} value={note} wide />
          </Field>
        </Disclosure>
      </FormSection>
      <ModalFooter>
        {failure === undefined ? null : (
          <p className="vissue addfoot__error" role="alert">
            {errorMessage(failure)}
            {failure.error_code === undefined ? null : (
              <>
                {' '}
                <span className="mono">{failure.error_code}</span>
              </>
            )}
          </p>
        )}
        <Button
          onClick={onClose}
          size="pill"
          {...(saving
            ? ({ disabled: true, disabledReason: t('reason.requestInFlight') } as const)
            : {})}
        >
          {t('monitoring.supply.replenish.no')}
        </Button>
        <Button onClick={submit} size="pill" variant="primary" {...yesGate}>
          {t('monitoring.supply.replenish.yes')}
        </Button>
      </ModalFooter>
    </AddModal>
  )
}
